import { useState, type FormEvent } from "react";
import { Button, Card, Input } from "@insights/ui";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router";
import { authClient } from "../../../api";
import { PageHeader } from "../../../shared/components";
import { formatMonthlyPrice } from "../../../shared/helpers/format-monthly-price";
import {
  AppShell,
  buildDefaultWorkspacePath,
} from "../../../shared/layout";
import { createWorkspaceSlug } from "../helpers/create-workspace-slug";
import { useWorkspaceCreation } from "../hooks/useWorkspaceCreation";
import type { SelectedPlan } from "../types/product";

export function CreateWorkspacePage() {
  const navigate = useNavigate();
  const organizations = authClient.useListOrganizations();
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SelectedPlan | null>(null);
  const [seats, setSeats] = useState(1);
  const creation = useWorkspaceCreation();

  async function create(event: FormEvent) {
    event.preventDefault();
    setError("");
    setSubmitting(true);
    if (!selectedPlan) {
      setError("Select a plan before creating the workspace");
      setSubmitting(false);
      return;
    }
    const input = {
      name,
      slug: createWorkspaceSlug(name),
      productId: selectedPlan.product.id,
      priceId: selectedPlan.price.id,
      seats,
    };
    try {
      if (selectedPlan.price.basePricePerMonth === 0) {
        const result = await creation.createFree.mutateAsync(input);
        await organizations.refetch();
        navigate(buildDefaultWorkspacePath(result.id), { replace: true });
      } else {
        const result = await creation.createCheckout.mutateAsync(input);
        window.location.assign(result.checkoutUrl);
      }
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Unable to create workspace",
      );
      setSubmitting(false);
    }
  }

  return (
    <AppShell>
      <PageHeader
        eyebrow={
          organizations.data?.length
            ? "Workspace administration"
            : "First workspace"
        }
        title={
          organizations.data?.length
            ? "Create a workspace"
            : "Welcome to Insights"
        }
      />
      <Card className="form-card workspace-create-card">
        <h2>Workspace details</h2>
        <p>
          Each workspace keeps its own members, teams, builds, billing, and
          integrations.
        </p>
        <form onSubmit={create}>
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Magnus Opera"
            maxLength={100}
            autoFocus
            required
          />
          <div className="plan-list">
            {creation.products.data?.items.flatMap((product) =>
              product.prices.map((price) => {
                const selected =
                  selectedPlan?.product.id === product.id &&
                  selectedPlan.price.id === price.id;
                const monthly = formatMonthlyPrice(
                  price,
                  product.currency,
                );
                const baseStorage = price.baseStorageMb;
                return (
                  <button
                    type="button"
                    className={`plan-option${selected ? " selected" : ""}`}
                    key={`${product.id}-${price.id}`}
                    onClick={() => {
                      setSelectedPlan({ product, price });
                      setSeats(
                        Math.max(price.baseSeats ?? 1, 1),
                      );
                    }}
                  >
                    <strong>{price.name}</strong>
                    <span>{product.description}</span>
                    <b>{monthly}</b>
                    <small>{baseStorage.toLocaleString()} MB included</small>
                  </button>
                );
              }),
            )}
          </div>
          {selectedPlan?.price.pricingModel === "per_seat" && (
            <label className="seat-selector">
              Seats
              <Input
                type="number"
                min={
                  selectedPlan.price.baseSeats ?? 1
                }
                max={selectedPlan.price.maxSeats ?? 20}
                value={seats}
                onChange={(event) => setSeats(Number(event.target.value))}
              />
            </label>
          )}
          <Button disabled={submitting} icon={<Plus />}>
            {submitting ? "Creating…" : "Create workspace"}
          </Button>
        </form>
        {error && <p className="error">{error}</p>}
        {creation.products.isLoading && <p>Loading available plans…</p>}
        {creation.products.error && (
          <p className="error">{creation.products.error.message}</p>
        )}
      </Card>
    </AppShell>
  );
}
